import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import { Trash2, Eye, Printer } from 'lucide-react';
import { formatCurrency, formatDate } from '../utils/helpers';

export default function Trade() {
  const [activeTab, setActiveTab] = useState('sales'); // 'sales' | 'purchases'

  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);

  const [viewSaleId, setViewSaleId] = useState(null);
  const [viewSaleData, setViewSaleData] = useState(null);
  const [viewPurchaseId, setViewPurchaseId] = useState(null);
  const [viewPurchaseData, setViewPurchaseData] = useState(null);

  const loadSales = () =>
    api.get('/sales').then((r) => setSales(r.data.data || []));
  const loadPurchases = () =>
    api.get('/purchases').then((r) => setPurchases(r.data.data || []));

  useEffect(() => {
    loadSales();
    loadPurchases();
  }, []);

  const delSale = async (id) => {
    if (!window.confirm('Delete sale? Stock will be restored.')) return;
    try {
      await api.delete('/sales/' + id);
      toast.success('Deleted');
      loadSales();
    } catch (e) {
      toast.error('Error');
    }
  };

  const delPurchase = async (id) => {
    if (!window.confirm('Delete purchase? Stock will be reversed.')) return;
    try {
      await api.delete('/purchases/' + id);
      toast.success('Deleted');
      loadPurchases();
    } catch (e) {
      toast.error('Error');
    }
  };

  const viewSale = async (id) => {
    const r = await api.get('/sales/' + id);
    setViewSaleData(r.data.data);
    setViewSaleId(id);
  };

  const viewPurchase = async (id) => {
    const r = await api.get('/purchases/' + id);
    setViewPurchaseData(r.data.data);
    setViewPurchaseId(id);
  };

  const markDelivered = async (id) => {
    try {
      await api.put('/sales/' + id, { delivery_status: 'delivered' });
      toast.success('Marked delivered');
      loadSales();
    } catch (e) {
      toast.error('Error');
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
          <h3 style={{ marginRight: 8 }}>Sales & Purchases</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className={'btn btn-sm ' + (activeTab === 'sales' ? 'btn-blue' : 'btn-gray')}
              onClick={() => setActiveTab('sales')}
              type="button"
            >
              Sales ({sales.length})
            </button>
            <button
              className={'btn btn-sm ' + (activeTab === 'purchases' ? 'btn-blue' : 'btn-gray')}
              onClick={() => setActiveTab('purchases')}
              type="button"
            >
              Purchases ({purchases.length})
            </button>
          </div>
        </div>
      </div>

      <div className="table-wrap">
        {activeTab === 'sales' ? (
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Ledger</th>
                <th>Vehicle</th>
                <th className="td-num">Total</th>
                <th className="td-num">Paid</th>
                <th className="td-num">Due</th>
                <th>Payment</th>
                <th>Delivery</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sales.length ? (
                sales.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <strong>{s.invoice_no}</strong>
                    </td>
                    <td>{formatDate(s.sale_date)}</td>
                    <td>{s.ledger_name}</td>
                    <td>{s.vehicle_no || '-'}</td>
                    <td className="td-num">{formatCurrency(s.total_amount)}</td>
                    <td className="td-num">{formatCurrency(s.paid_amount)}</td>
                    <td className="td-num text-red">{formatCurrency(s.due_amount)}</td>
                    <td>
                      <span
                        className={
                          'badge ' +
                          (s.payment_status === 'paid'
                            ? 'badge-green'
                            : s.payment_status === 'partial'
                              ? 'badge-orange'
                              : 'badge-red')
                        }
                      >
                        {s.payment_status}
                      </span>
                    </td>
                    <td>
                      {s.delivery_status !== 'delivered' ? (
                        <button
                          onClick={() => markDelivered(s.id)}
                          className="btn btn-sm btn-green"
                          title="Mark delivered"
                        >
                          <span className="badge badge-orange">{s.delivery_status}</span>
                        </button>
                      ) : (
                        <span className="badge badge-green">delivered</span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-gray" onClick={() => viewSale(s.id)}>
                        <Eye size={12} />
                      </button>{' '}
                      <button className="btn btn-sm btn-red" onClick={() => delSale(s.id)}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="empty">
                    No sales
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Date</th>
                <th>Ledger</th>
                <th>Vehicle</th>
                <th className="td-num">Total</th>
                <th className="td-num">Paid</th>
                <th className="td-num">Due</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length ? (
                purchases.map((p) => (
                  <tr key={p.id}>
                    <td>
                      <strong>{p.invoice_no}</strong>
                    </td>
                    <td>{formatDate(p.purchase_date)}</td>
                    <td>{p.ledger_name}</td>
                    <td>{p.vehicle_no || '-'}</td>
                    <td className="td-num">{formatCurrency(p.total_amount)}</td>
                    <td className="td-num">{formatCurrency(p.paid_amount)}</td>
                    <td className="td-num text-red">{formatCurrency(p.due_amount)}</td>
                    <td>
                      <span
                        className={
                          'badge ' +
                          (p.payment_status === 'paid'
                            ? 'badge-green'
                            : p.payment_status === 'partial'
                              ? 'badge-orange'
                              : 'badge-red')
                        }
                      >
                        {p.payment_status}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm btn-gray" onClick={() => viewPurchase(p.id)}>
                        <Eye size={12} />
                      </button>{' '}
                      <button className="btn btn-sm btn-red" onClick={() => delPurchase(p.id)}>
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="empty">
                    No purchases
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* View Sale Invoice */}
      <Modal
        open={!!viewSaleId}
        onClose={() => {
          setViewSaleId(null);
          setViewSaleData(null);
        }}
        title={'Invoice ' + (viewSaleData?.invoice_no || '')}
        size="lg"
        footer={
          <>
            <button
              className="btn btn-gray"
              onClick={() => {
                setViewSaleId(null);
                setViewSaleData(null);
              }}
            >
              Close
            </button>
            <button className="btn btn-blue" onClick={() => window.print()}>
              <Printer size={13} /> Print Invoice
            </button>
          </>
        }
      >
        {viewSaleData && (
          <div className="invoice-print">
            <div className="invoice-head">
              <h1>TAX INVOICE</h1>
              <p>
                #{viewSaleData.invoice_no} · {formatDate(viewSaleData.sale_date)}
              </p>
            </div>
            <div className="inv-grid">
              <div>
                <div className="inv-label">Bill To</div>
                <div className="inv-value">
                  <strong>{viewSaleData.ledger_name}</strong>
                </div>
                <div style={{ whiteSpace: 'pre-line' }}>
                  {viewSaleData.delivery_address || viewSaleData.ledger_address}
                </div>
                <div>
                  {viewSaleData.ledger_phone}{' '}
                  {viewSaleData.ledger_gstin && '· GST: ' + viewSaleData.ledger_gstin}
                </div>
              </div>
              <div>
                <div className="inv-label">Delivery</div>
                <div className="inv-value">Vehicle: {viewSaleData.vehicle_no || 'N/A'}</div>
                <div>Driver: {viewSaleData.driver_name || '-'}</div>
                <div className="inv-label" style={{ marginTop: 10 }}>
                  Status
                </div>
                <div className="inv-value" style={{ textTransform: 'capitalize' }}>
                  {viewSaleData.delivery_status} · Payment: {viewSaleData.payment_status}
                </div>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
              <thead>
                <tr style={{ background: '#059669', color: '#fff' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Product</th>
                  <th style={{ padding: 8 }}>Qty</th>
                  <th style={{ padding: 8 }}>Rate</th>
                  <th style={{ padding: 8 }}>Tax</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {viewSaleData.items?.map((it) => (
                  <tr key={it.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 8 }}>{it.product_name}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {it.quantity} {it.unit}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(it.rate)}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{it.tax_rate}%</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(it.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Subtotal
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(viewSaleData.subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Tax
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(viewSaleData.tax_amount)}</td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Transport
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    {formatCurrency(viewSaleData.transport_charges)}
                  </td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Discount
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>-{formatCurrency(viewSaleData.discount)}</td>
                </tr>
                <tr className="inv-total-row">
                  <td colSpan="4" style={{ padding: 10, textAlign: 'right' }}>
                    <strong>GRAND TOTAL</strong>
                  </td>
                  <td style={{ padding: 10, textAlign: 'right' }}>
                    <strong>{formatCurrency(viewSaleData.total_amount)}</strong>
                  </td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Paid
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(viewSaleData.paid_amount)}</td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    <strong>Balance Due</strong>
                  </td>
                  <td style={{ padding: 8, textAlign: 'right', color: '#dc2626' }}>
                    <strong>{formatCurrency(viewSaleData.due_amount)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
            {viewSaleData.notes && (
              <p style={{ marginTop: 14, fontSize: 12, color: '#64748b' }}>Notes: {viewSaleData.notes}</p>
            )}
            <p style={{ marginTop: 30, textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
              Thank you for your business!
            </p>
          </div>
        )}
      </Modal>

      {/* View Purchase Invoice */}
      <Modal
        open={!!viewPurchaseId}
        onClose={() => {
          setViewPurchaseId(null);
          setViewPurchaseData(null);
        }}
        title={'Purchase ' + (viewPurchaseData?.invoice_no || '')}
        size="lg"
        footer={
          <>
            <button
              className="btn btn-gray"
              onClick={() => {
                setViewPurchaseId(null);
                setViewPurchaseData(null);
              }}
            >
              Close
            </button>
            <button className="btn btn-blue" onClick={() => window.print()}>
              <Printer size={13} /> Print
            </button>
          </>
        }
      >
        {viewPurchaseData && (
          <div className="invoice-print">
            <div className="invoice-head">
              <h1>PURCHASE INVOICE</h1>
              <p>
                #{viewPurchaseData.invoice_no} · {formatDate(viewPurchaseData.purchase_date)}
              </p>
            </div>
            <div className="inv-grid">
              <div>
                <div className="inv-label">Ledger</div>
                <div className="inv-value">
                  <strong>{viewPurchaseData.ledger_name}</strong>
                </div>
                <div>{viewPurchaseData.ledger_address}</div>
                <div>
                  {viewPurchaseData.ledger_phone}{' '}
                  {viewPurchaseData.ledger_gstin && '· GST: ' + viewPurchaseData.ledger_gstin}
                </div>
              </div>
              <div>
                <div className="inv-label">Transport</div>
                <div className="inv-value">{viewPurchaseData.vehicle_no || 'N/A'}</div>
                <div className="inv-label" style={{ marginTop: 10 }}>
                  Payment Status
                </div>
                <div className="inv-value" style={{ textTransform: 'capitalize' }}>
                  {viewPurchaseData.payment_status} ({viewPurchaseData.payment_mode})
                </div>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10 }}>
              <thead>
                <tr style={{ background: '#1e40af', color: '#fff' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Product</th>
                  <th style={{ padding: 8 }}>Qty</th>
                  <th style={{ padding: 8 }}>Rate</th>
                  <th style={{ padding: 8 }}>Tax</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {viewPurchaseData.items?.map((it) => (
                  <tr key={it.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: 8 }}>{it.product_name}</td>
                    <td style={{ padding: 8, textAlign: 'center' }}>
                      {it.quantity} {it.unit}
                    </td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(it.rate)}</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{it.tax_rate}%</td>
                    <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(it.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Subtotal
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(viewPurchaseData.subtotal)}</td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Tax
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(viewPurchaseData.tax_amount)}</td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Transport
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>
                    {formatCurrency(viewPurchaseData.transport_charges)}
                  </td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Discount
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>-{formatCurrency(viewPurchaseData.discount)}</td>
                </tr>
                <tr className="inv-total-row">
                  <td colSpan="4" style={{ padding: 10, textAlign: 'right' }}>
                    <strong>GRAND TOTAL</strong>
                  </td>
                  <td style={{ padding: 10, textAlign: 'right' }}>
                    <strong>{formatCurrency(viewPurchaseData.total_amount)}</strong>
                  </td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    Paid
                  </td>
                  <td style={{ padding: 8, textAlign: 'right' }}>{formatCurrency(viewPurchaseData.paid_amount)}</td>
                </tr>
                <tr>
                  <td colSpan="4" style={{ padding: 8, textAlign: 'right' }}>
                    <strong>Due</strong>
                  </td>
                  <td style={{ padding: 8, textAlign: 'right', color: '#dc2626' }}>
                    <strong>{formatCurrency(viewPurchaseData.due_amount)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
            {viewPurchaseData.notes && (
              <p style={{ marginTop: 14, fontSize: 12, color: '#64748b' }}>Notes: {viewPurchaseData.notes}</p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
